// ============================================================================
// modules/parametros/pages/ParametrosPage.jsx
// Página (composer) del listado de Parámetros. Migrada desde
// src/pages/Seguridad/Parametro/Parametros.jsx. Los datos se orquestan vía
// useParametros() → api/parametros.api → httpClient. La lógica de UI/permisos
// se mantiene idéntica para no perder funcionalidad.
//
// Dependencias cross-módulo (companias, validación de auth, catálogos) siguen
// apuntando a las ubicaciones legacy; en la migración completa se consumen vía
// el public API de cada módulo (modules/usuarios, shared/*). Ver plan §Fases.
// ============================================================================

//Iconos
import { FaRegEdit } from "react-icons/fa"
import { RiDeleteBinFill } from "react-icons/ri"
import { IoMdAdd } from "react-icons/io"
import { BiSearch } from "react-icons/bi"

import { useNavigate } from "react-router-dom"
import { listaEstadosActivo, listaEstadosActivoBusqueda } from "../../../app/constants/constants-selectores"
import { useForm } from "react-hook-form"
import { DataContext } from "../../../app/providers/DataContext"
import { useContext, useEffect, useState } from "react"
import { obtenerLabel, obtenerLocalStorage } from "../../../shared/utils/funciones"
import Swal from 'sweetalert2';
import { useMobileDetection } from "../../../shared/hooks/useMobileDetection"
import { CONST_KEY_USUARIO_AUTENTICADO } from "../../../app/constants/constanst-valores"
import { CONST_CODIGO_COMPANIAS_EDITAR, CONST_CODIGO_PARAMETROS_AGREGAR, CONST_CODIGO_PARAMETROS_EDITAR, CONST_CODIGO_PARAMETROS_ELIMINAR, CONST_RUTA_PARAMETROS_AGREGAR, CONST_RUTA_PARAMETROS_EDITAR, CONST_RUTA_PARAMETROS_LISTAR, CONST_RUTA_SIN_ACCESO } from "../../../app/constants/constants-rutas-codigos"
import { validarAutenticacion } from "../../auth"
import { useRefetchAlCerrarModal } from "../../../shared/hooks/useRefetchAlCerrarModal"
import { usuarioObtenerCompaniasYSucursales } from "../../usuarios"

import { useParametros } from "../hooks/useParametros"
import { TablaSkeleton } from "../../../shared/ui/TablaSkeleton";

export const ParametrosPage = () => {

    const usuarioAutenticado = obtenerLocalStorage(CONST_KEY_USUARIO_AUTENTICADO);

    const { listaRutasDeAcceso } = useContext(DataContext)
    const [listaCompanias, setListaCompanias] = useState<any[]>([])

    // Estado de datos orquestado por el hook de caso de uso del módulo.
    const { listaParametros, isLoading, buscado, buscarParametros, obtenerParametro, eliminarParametro } = useParametros()

    //Estados para ver si mostrar o no los botones
    const [mostrarBotonAgregar, setMostrarBotonAgregar] = useState(false)
    const [mostrarBotonEditar, setMostrarBotonEditar] = useState(false)
    const [mostrarBotonEliminar, setMostrarBotonEliminar] = useState(false)

    const { register, handleSubmit, watch, setValue } = useForm<Record<string, any>>({
        defaultValues: {
            codigo: "",
            descripcion: "",
            estado: "A"
        }
    });


    const isMobile = useMobileDetection()

    const onSubmitBuscar = async (data) => {
        const dataPOST = {
            ParamCod: data?.codigo,
            ParamDes: data?.descripcion,
            estado: data?.estado,
            compania: data?.compania,
            token: usuarioAutenticado?.token

        }

        await buscarParametros(dataPOST)
    };

    const navigate = useNavigate()

    const parametroAgregar = async () => {

        const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, CONST_CODIGO_PARAMETROS_AGREGAR)

        if (validarAutenticado) {
            navigate(CONST_RUTA_PARAMETROS_AGREGAR)
        } else {
            navigate(CONST_RUTA_SIN_ACCESO)
        }

    }

    const parametroEditar = async (codParametro, codCompania) => {

        const validarAutenticado = await validarAutenticacion(usuarioAutenticado?.token, CONST_CODIGO_COMPANIAS_EDITAR)

        if (validarAutenticado) {
            const dataPOST = {
                codigo: codParametro,
                compania: codCompania,
                token: usuarioAutenticado?.token
            }

            const parametro = await obtenerParametro(dataPOST)

            navigate(CONST_RUTA_PARAMETROS_EDITAR, {
                state: parametro
            })
        } else {
            navigate(CONST_RUTA_SIN_ACCESO)
        }

    }

    const parametrosBuscarActivos = async () => {
        const dataPOST = {
            ParamCod: watch("codigo") || "",
            ParamDes: watch("descripcion") || "",
            // Fix: el campo del form es `estado` (minúscula, ver register("estado") + defaultValues).
            // Antes leía watch("Estado") (mayúscula, inexistente) → el refetch por watch (carga inicial,
            // post-eliminar, cierre de modal) usaba SIEMPRE "A", ignorando el filtro de estado elegido.
            estado: watch("estado") || "A",
            compania: watch("compania"),
            token: usuarioAutenticado?.token

        }

        await buscarParametros(dataPOST)
    }

    const optionsEliminar = (descripcionParametro) => {
        return {
            title: `¿Estás seguro de eliminar el parámetro: ${descripcionParametro}?`,
            icon: 'warning' as const,
            showCancelButton: true,
            cancelButtonColor: '#6c757d',
            confirmButtonColor: '#d33',
            confirmButtonText: 'Eliminar'
        }
    }

    // `codCompania` es obligatorio: el ParamCod se repite entre compañías (la PK de
    // TMPARAM10 es ParamCod + CompCod), así que sin él el backend daría de baja el
    // parámetro de TODAS. El backend rechaza el request si falta.
    const parametroEliminar = (codParametro, descripcionParametro, codCompania) => {
        Swal.fire(optionsEliminar(descripcionParametro)).then(async (result) => {
            if (result.isConfirmed) {
                const dataPOST = {
                    ParamCod: codParametro,
                    CompCod: codCompania,
                    token: usuarioAutenticado?.token

                }
                const { status } = await eliminarParametro(dataPOST)
                if (status === 1) {
                    parametrosBuscarActivos()
                    Swal.fire(
                        'Parámetro eliminado',
                        '',
                        'success'
                    )
                } else {
                    Swal.fire(
                        'Error al eliminar parámetro',
                        '',
                        'success'
                    )
                }

            }
        })

    }


    const obtenerCompaniasYSucursales = async () => {

        const dataPOST = {
            token: usuarioAutenticado?.token
        }

        const { companias, sucursales } = await usuarioObtenerCompaniasYSucursales(dataPOST)
        setListaCompanias(companias)
        setValue("compania", companias?.[0]?.id)

        return {
            companias,
            sucursales
        }
    }

    useEffect(() => {
        const encontradoRutaBotonAgregar = listaRutasDeAcceso?.find(r => String(r.codigo_programa) === String(CONST_CODIGO_PARAMETROS_AGREGAR))
        const encontradoRutaBotonEditar = listaRutasDeAcceso?.find(r => String(r.codigo_programa) === String(CONST_CODIGO_PARAMETROS_EDITAR))
        const encontradoRutaBotonEliminar = listaRutasDeAcceso?.find(r => String(r.codigo_programa) === String(CONST_CODIGO_PARAMETROS_ELIMINAR))

        encontradoRutaBotonAgregar ? setMostrarBotonAgregar(true) : setMostrarBotonAgregar(false)
        encontradoRutaBotonEditar ? setMostrarBotonEditar(true) : setMostrarBotonEditar(false)
        encontradoRutaBotonEliminar ? setMostrarBotonEliminar(true) : setMostrarBotonEliminar(false)
    }, [listaRutasDeAcceso])

    useEffect(() => {

        const cargarDatosIniciales = async () => {
            await obtenerCompaniasYSucursales()
            await parametrosBuscarActivos()
        };

        cargarDatosIniciales();
        // efecto intencional (no re-ejecutar por deps faltantes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Al cerrar el modal (agregar/editar) se refresca el listado montado de fondo.
    useRefetchAlCerrarModal(CONST_RUTA_PARAMETROS_LISTAR, parametrosBuscarActivos)

    return (
        <div className="p-3">
            <div className="container-fluid">
                <h3 className="tw-text-center tw-text-2xl tw-font-semibold tw-text-menu-primario tw-mt-1 tw-mb-4">Parámetros</h3>

                {/* Formulario */}
                <div className="tw-mb-4 tw-rounded-2xl tw-bg-white tw-p-4 tw-shadow-md tw-border tw-border-gray-100">
                    <form className="row g-2" onSubmit={handleSubmit(onSubmitBuscar)}>

                        <div className="col-12 col-lg-3">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Código</label>
                            <input
                                {...register("codigo")}
                                type="text"
                                className="form-control form-control-sm"
                                autoComplete="off"
                                placeholder="Código"
                            />
                        </div>
                        <div className="col-12 col-lg-3">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Descripción</label>
                            <input
                                {...register("descripcion")}
                                type="text"
                                className="form-control form-control-sm"
                                autoComplete="off"
                                placeholder="Descripción"
                            />
                        </div>
                        <div className="col-12 col-lg-3">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Compañía</label>
                            <select
                            {...register("compania")}
                            className="form-select form-select-sm"
                            value={watch("compania", {
                                required: true
                            })}
                            >
                                {
                                    listaCompanias?.length > 0 && listaCompanias.map(e => (
                                        <option key={e?.id} value={e?.id}>{e?.tipo}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className="col-12 col-lg-2">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">Estado</label>
                            <select {...register("estado")} className="form-select form-select-sm" >
                                {
                                    listaEstadosActivoBusqueda?.length > 0 && listaEstadosActivoBusqueda.map(e => (
                                        <option key={e?.id} value={e?.id}>{e?.tipo}</option>
                                    ))
                                }
                            </select>
                        </div>

                        <div className="col-12 col-lg-1">
                            <label className="text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1 tw-block">&nbsp;</label>
                            <div className={isMobile ? "text-center" : ""}>
                                <button type="submit" onClick={handleSubmit(onSubmitBuscar)} style={{ cursor: 'pointer', background: 'none', border: 'none' }}>
                                    <BiSearch size={25} className="color-iconos" />
                                </button>
                            </div>
                        </div>

                    </form>
                </div>
                {/* Fin formulario */}

                {
                    mostrarBotonAgregar && (
                        <div className="mx-auto ps-0">
                            <button className='btn btn-primario btn-sm tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-shadow-sm hover:tw-shadow-md tw-transition-shadow' onClick={parametroAgregar}>
                                <IoMdAdd size={18} />
                                <span className="ms-1">Agregar</span>
                            </button>
                        </div>
                    )
                }
                {/* Tabla de resultados */}
                <div className="tw-mt-2 tw-rounded-2xl tw-bg-white tw-p-3 tw-shadow-md tw-border tw-border-gray-100 tw-overflow-x-auto">

                    <table className="table table-hover tw-mb-0">
                        <thead className="table-light">
                            <tr>
                                <th scope="col" className="col text-center">Código</th>
                                <th scope="col" className="col text-center">Descripción</th>
                                <th scope="col" className="col text-center">Valor</th>
                                <th scope="col" className="col text-center">Estado</th>
                                {
                                    mostrarBotonEditar && (
                                        <th scope="col" className="col text-center">Editar</th>
                                    )
                                }
                                {
                                    mostrarBotonEliminar && (
                                        <th scope="col" className="col text-center">Eliminar</th>
                                    )
                                }

                            </tr>
                        </thead>
                        <tbody className={isLoading ? "" : "tabla-fade-in"}>
                            {
                                (isLoading && !listaParametros?.length) ? (
                                    <TablaSkeleton rows={6} cols={4} />

                                ) : (
                                    listaParametros?.length > 0 ? listaParametros.map(m => (
                                        <tr key={m?.ParamCod}>
                                            <td>{m?.ParamCod}</td>
                                            <td>{m?.ParamDes}</td>
                                            <td>{m?.ParamVal}</td>
                                            <td>{obtenerLabel(listaEstadosActivo, m?.ParamEst)}</td>
                                            {
                                                mostrarBotonEditar && (
                                                    <td><FaRegEdit size={23} cursor={'pointer'} className="color-iconos" onClick={() => parametroEditar(m?.ParamCod, m?.CompCod)} /></td>
                                                )
                                            }
                                            {
                                                mostrarBotonEliminar && (
                                                    <td><RiDeleteBinFill size={23} cursor={'pointer'} color="#C0392B" onClick={() => parametroEliminar(m?.ParamCod, m?.ParamDes, m?.CompCod)} /></td>
                                                )
                                            }
                                        </tr>
                                    )) :
                                        (
                                            (buscado) ? (
                                                <tr>
                                                    <td colSpan={100} className="text-center">No hay parámetros</td>
                                                </tr>
                                            ) : (
                                                <tr>
                                                    <td colSpan={100} className="text-center">Realice una búsqueda</td>
                                                </tr>
                                            )
                                        )
                                )
                            }
                        </tbody>
                    </table>

                </div>
                {/* Fin Tabla de resultados */}

            </div>
        </div>
    )
}

// Alias retro-compatible: el resto del app importa `Parametros`.
export const Parametros = ParametrosPage
